import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/Navigation';
import { 
  TrendingUp, 
  LayoutDashboard, 
  BookOpen, 
  BarChart2, 
  FileText, 
  Calendar, 
  User, 
  LogOut,
  Shield,
  Star,
  Home
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function DashboardLayout() {
  const { user, logout, isAdmin } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: t('dashboard.overview'), icon: LayoutDashboard },
    { path: '/dashboard/courses', label: t('dashboard.myCourses'), icon: BookOpen },
    { path: '/dashboard/watchlist', label: t('dashboard.watchlist'), icon: Star },
    { path: '/dashboard/resources', label: t('dashboard.resources'), icon: FileText },
    { path: '/dashboard/consulting', label: t('dashboard.bookConsulting'), icon: Calendar },
    { path: '/dashboard/profile', label: t('dashboard.profile'), icon: User },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', label: t('dashboard.adminPanel'), icon: Shield });
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#141417] border-r border-[#27272A] flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-[#27272A]">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
            <div className="w-8 h-8 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              COSSY NETWORK
            </span>
          </Link>
        </div>

        {/* Back to Home Button - Prominent */}
        <div className="p-4 border-b border-[#27272A]">
          <Link to="/">
            <Button className="w-full btn-secondary flex items-center justify-center gap-2" data-testid="sidebar-back-home">
              <Home className="w-4 h-4" /> {t('nav.backToHome')}
            </Button>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Language Selector */}
        <div className="px-4 py-2 border-t border-[#27272A]">
          <LanguageSelector />
        </div>

        {/* User Section */}
        <div className="p-4 border-t border-[#27272A]">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-[#71717A] text-sm truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-[#1D1D21]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
